import React from 'react';
import { PositionStatistics } from '../../services/accountService';
import { ProfitTimeRange } from '@/services/enums';
import SelectDropdown from '../SelectDropdown';

interface StatsModuleProps {
  stats: PositionStatistics;
  loading: boolean;
  timeRange: ProfitTimeRange;
  onTimeRangeChange: (range: ProfitTimeRange) => void;
}

const SummaryStatsModule: React.FC<StatsModuleProps> = ({ stats, loading, timeRange, onTimeRangeChange }) => {
  const timeRangeOptions = [
    { value: '7d', label: '最近 7 天汇总' },
    { value: '15d', label: '最近 15 天汇总' },
    { value: '30d', label: '最近 30 天汇总' },
    { value: '90d', label: '最近 90 天汇总' },
    { value: 'all', label: '全部' },
  ];
  if (loading) {
    return (
      <div className="bg-white/5 border border-white/20 rounded-xl p-4 mb-6 animate-pulse">
        <div className="h-6 w-32 bg-white/10 rounded mb-4"></div>
        <div className="grid grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-white/5 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  const formatNumber = (num: number, decimals: number = 2) => {
    if (num === undefined || num === null) return '-';
    return num.toFixed(decimals);
  };

  const getPnLColor = (num: number) => {
    if (num > 0) return 'text-green-400';
    if (num < 0) return 'text-red-400';
    return 'text-white';
  };

  return (
    <div className="bg-white/5 border border-white/20 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <SelectDropdown
            value={timeRange}
            onChange={(val) => onTimeRangeChange(val as ProfitTimeRange)}
            options={timeRangeOptions}
            className="mb-0 border-none bg-transparent"
          />
        </div>
        <span className="text-white/40 text-xs">基于历史平仓记录</span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white/5 rounded-lg p-3 border border-white/5">
          <div className="text-white/50 text-xs mb-1">总交易数</div>
          <div className="text-lg font-bold text-white">{stats.totalTrades}</div>
        </div>

        <div className="bg-white/5 rounded-lg p-3 border border-white/5">
          <div className="text-white/50 text-xs mb-1">胜率</div>
          <div className={`text-lg font-bold ${stats.winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
            {formatNumber(stats.winRate, 1)}%
          </div>
        </div>

        <div className="bg-white/5 rounded-lg p-3 border border-white/5">
          <div className="text-white/50 text-xs mb-1">平均盈亏比</div>
          <div className="text-lg font-bold text-white">{formatNumber(stats.avgProfitLossRatio, 2)}</div>
        </div>

        <div className="bg-white/5 rounded-lg p-3 border border-white/5">
          <div className="text-white/50 text-xs mb-1">手续费总额</div>
          <div className="text-lg font-bold text-red-300">-{formatNumber(stats.totalFee, 2)}</div>
        </div>

        <div className="bg-white/5 rounded-lg p-3 border border-white/5">
          <div className="text-white/50 text-xs mb-1">资金费总额</div>
          <div className={`text-lg font-bold ${getPnLColor(stats.totalFundingFee)}`}>
            {stats.totalFundingFee > 0 ? '+' : ''}
            {formatNumber(stats.totalFundingFee, 2)}
          </div>
        </div>

        <div className="bg-white/5 rounded-lg p-3 border border-white/5">
          <div className="text-white/50 text-xs mb-1">最大回撤</div>
          <div className="text-lg font-bold text-red-400">{formatNumber(stats.maxDrawdown, 2)}%</div>
        </div>
      </div>
    </div>
  );
};

export default SummaryStatsModule;
